/**
 * Business profile — design/PROTOTYPE-SPEC.md §16.
 *
 * The parts a clinic can change about itself, minus two that are blocked on
 * decisions rather than on effort:
 *
 *   - **Logo upload** needs an object store: an S3 bucket, an upload policy, and
 *     size/type limits. `accounts.logo_key` is already in the schema waiting for
 *     it. Scoping that properly is a separate piece of work — see
 *     `docs/REMAINING-SCOPE.md` §A16.
 *   - **Notification settings** need something to schedule against. Offering the
 *     switches before the digest exists would be a settings page whose controls
 *     do nothing, which is worse than not having it.
 */
import { z } from 'zod';
/**
 * VAT is stored in basis points, so 20% is 2000 — the same reason money is
 * integer pence. The UI shows a percentage and converts at the edge.
 *
 * The cap is 100%, not 20%: UK VAT is 20% today, but a rate is a number a
 * business sets, and hardcoding this year's value into a validator is how you
 * get a bug report the day it changes.
 */
export const VAT_RATE_MAX_BPS = 10_000;
export const vatRateBpsSchema = z
    .number()
    .int('VAT rate must be a whole number of basis points')
    .min(0, 'VAT rate cannot be negative')
    .max(VAT_RATE_MAX_BPS, 'VAT rate cannot exceed 100%');
/**
 * Time zones are validated against the runtime's own database rather than a
 * hardcoded list, which would rot. `Europe/London` is the default everywhere
 * else in the product; this exists because a clinic in another zone would
 * otherwise see expiry dates roll over at the wrong hour.
 */
export const timeZoneSchema = z.string().refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, { message: 'Not a recognised time zone' });
/**
 * Every field optional — this is a PATCH, and a form that only changed the
 * tagline must not have to resend the clinic name. The route rejects an empty
 * object rather than reporting a successful no-op.
 */
export const updateAccountSchema = z
    .object({
    clinicName: z
        .string()
        .trim()
        .min(1, 'Business name cannot be empty')
        .max(120, 'Business name is too long')
        .optional(),
    // Nullable, not just optional: clearing a tagline is a thing someone does,
    // and `undefined` cannot express it in a PATCH.
    tagline: z.string().trim().max(200, 'Tagline is too long').nullable().optional(),
    vatRateBps: vatRateBpsSchema.optional(),
    timeZone: timeZoneSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/** Percentage for display, basis points for storage. 2000 ⇄ 20. */
export const bpsToPercent = (bps) => bps / 100;
export const percentToBps = (percent) => Math.round(percent * 100);
// ----------------------------------------------------------------------- logo
/**
 * Raster formats only. **SVG is excluded on purpose**: an SVG is a document
 * that can carry script, and this image is served back from the API's own
 * origin to every signed-in member of the clinic.
 */
export const LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
/** Decoded size. The browser resizes to 512px first, so real logos are far smaller. */
export const LOGO_MAX_BYTES = 256 * 1024;
/** Longest edge the browser resizes to before upload. */
export const LOGO_MAX_EDGE_PX = 512;
export const uploadLogoSchema = z.object({
    contentType: z.enum(LOGO_CONTENT_TYPES),
    /** Base64 of the image bytes. 4/3 of the byte cap, plus padding. */
    dataBase64: z
        .string()
        .min(1)
        .max(Math.ceil((LOGO_MAX_BYTES * 4) / 3) + 4)
        .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Not a valid image'),
});
/**
 * What the bytes actually are, from their first few — never from the filename
 * or the type the client claimed. A file called logo.png containing HTML must
 * be refused, because the API will later serve it with whatever type it stored.
 */
export function sniffImageType(bytes) {
    const starts = (...sig) => sig.every((b, i) => bytes[i] === b);
    if (starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a))
        return 'image/png';
    if (starts(0xff, 0xd8, 0xff))
        return 'image/jpeg';
    // RIFF....WEBP
    if (starts(0x52, 0x49, 0x46, 0x46) &&
        bytes[8] === 0x57 &&
        bytes[9] === 0x45 &&
        bytes[10] === 0x42 &&
        bytes[11] === 0x50) {
        return 'image/webp';
    }
    return null;
}
// -------------------------------------------------------------------- contact
/**
 * design/Clinic Stock v2.dc.html:6266 — the Business profile's contact and
 * registration fields. All optional: a sole practitioner has no company number
 * and many clinics are not VAT registered.
 *
 * Free text, lightly bounded. A phone number is not validated as a phone number
 * and a postcode is not validated as a postcode — the clinic knows its own
 * details better than a regular expression does, and a form that rejects a
 * correct address is worse than one that accepts a typo.
 */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max, 'That is too long')
    // An emptied box means "remove this", so it is stored as absent.
    .transform((v) => (v === '' ? undefined : v))
    .optional();
export const CONTACT_FIELDS = [
    'phone',
    'website',
    'line1',
    'line2',
    'city',
    'postcode',
    'companyNumber',
    'vatNumber',
];
export const CONTACT_FIELD_LABEL = {
    phone: 'Phone',
    website: 'Website',
    line1: 'Address line 1',
    line2: 'Address line 2',
    city: 'Town or city',
    postcode: 'Postcode',
    companyNumber: 'Company number',
    vatNumber: 'VAT number',
};
export const contactDetailsSchema = z
    .object({
    phone: optionalText(40),
    website: optionalText(200),
    line1: optionalText(120),
    line2: optionalText(120),
    city: optionalText(80),
    postcode: optionalText(12),
    companyNumber: optionalText(20),
    vatNumber: optionalText(20),
})
    .strict();
//# sourceMappingURL=schemas.js.map